import { Modal, useModals } from 'almanac-ui/Modal'
import { ConfirmModal } from 'blaze-components/ConfirmModal/ConfirmModal'
import { Button } from 'blaze-ui/Button'
import { ArrowRefresh2, LinkExternal } from 'blaze-ui/icons'
import { Input } from 'almanac-ui/TextInputField/Input'
import { LineSkeleton } from 'components/Skeleton/LineSkeleton'
import { useEffect, useMemo, useRef, useState } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { CONTENT_PREFERENCES_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import Styles from './EditCaptionModal.module.scss'
import cx from 'classnames'
import { useGenerateCaption } from 'hooks/queries/scheduledPosts/useGenerateCaption'
import { PostLocation, PostTag, ScheduledPostTarget } from 'types'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import twitter from 'twitter-text'
import { maxCharacters } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets'
import { useCreateScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useCreateScheduledPostTarget'
import { useDeleteScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useDeleteScheduledPostTarget'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import {
  DYNAMIC_CAPTION_PROVIDERS,
  DynamicCaptionAccountItem,
  DynamicCaptionProvider,
  DynamicCaptionRow,
  DynamicCaptionsModalContent,
} from './DynamicCaptionsModalContent'
import { getCrossPostGroupForChannel } from 'screens/Calendar/FullScreenPreview/utils'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'

interface EditCaptionModalProps {
  caption: string
  onSave: (payload: EditCaptionSavePayload) => Promise<void> | void
  scheduledPostId: number
  scheduledPostTargets?: ScheduledPostTarget[]
  sourceChannel?: string
}

export type EditCaptionSavePayload =
  | string
  | {
      type: 'dynamic'
      captionsByProvider: Partial<Record<ExternalIntegrationEnum, string>>
      captionsByTargetId: Partial<Record<number, string>>
      locationsByTargetId: Partial<Record<number, PostLocation | null>>
      tagsByTargetId: Partial<Record<number, PostTag[]>>
      targets: ScheduledPostTarget[]
    }

export const PROVIDER_LABELS: Record<DynamicCaptionProvider, string> = {
  [ExternalIntegrationEnum.Instagram]: 'Instagram',
  [ExternalIntegrationEnum.Facebook]: 'Facebook',
  [ExternalIntegrationEnum.LinkedIn]: 'LinkedIn',
  [ExternalIntegrationEnum.X]: 'X/Twitter',
  [ExternalIntegrationEnum.YouTube]: 'YouTube',
  [ExternalIntegrationEnum.TikTok]: 'TikTok',
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 'Google',
}

export const PROVIDER_DESCRIPTIONS: Record<DynamicCaptionProvider, string> = {
  [ExternalIntegrationEnum.Instagram]: 'Conversational, expressive, uses emojis and short sentences',
  [ExternalIntegrationEnum.Facebook]: 'Balanced between personal and informative; encourages comments',
  [ExternalIntegrationEnum.LinkedIn]: 'Formal but human; focuses on expertise, leadership, and value',
  [ExternalIntegrationEnum.X]: 'Short, punchy, often uses humor or trending language',
  [ExternalIntegrationEnum.YouTube]: 'Descriptive and keyword-rich; optimized for search and discovery',
  [ExternalIntegrationEnum.TikTok]: 'Trendy, casual, and engaging; uses hashtags and hooks',
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 'Clear and local; highlights updates and offers for customers',
}

const isDynamicCaptionProvider = (provider: ExternalIntegrationEnum): provider is DynamicCaptionProvider =>
  DYNAMIC_CAPTION_PROVIDERS.includes(provider as DynamicCaptionProvider)

const getCaptionForTarget = (target: ScheduledPostTarget, fallbackCaption: string) => {
  const targetCaption = target.target_overrides?.content
  return targetCaption ? targetCaption : fallbackCaption
}

const getCharacterStats = (provider: ExternalIntegrationEnum, caption: string) => {
  const limit = maxCharacters[provider]
  const count = provider === ExternalIntegrationEnum.X ? twitter.parseTweet(caption).weightedLength : caption.length

  return {
    count,
    limit,
    isOverLimit: count > limit,
  }
}

const getIntegrationForTarget = (target: ScheduledPostTarget, externalIntegrations: ExternalIntegration[]) => {
  return externalIntegrations.find((integration) => integration.id === target.external_integration_id)
}

export const EditCaptionModal = ({
  caption,
  onSave,
  scheduledPostId,
  scheduledPostTargets,
  sourceChannel,
}: EditCaptionModalProps) => {
  const [localCaption, setLocalCaption] = useState(caption)
  const [localTargets, setLocalTargets] = useState(scheduledPostTargets ?? [])
  const { closeModal, closeAllModals, openModal } = useModals()
  const dynamicCaptionsEnabled = useFlipperFlag('dynamic_captions')
  const [isLegacyGenerating, setIsLegacyGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [generatingTargets, setGeneratingTargets] = useState<Partial<Record<number, boolean>>>({})
  const [updatingProviders, setUpdatingProviders] = useState<Partial<Record<DynamicCaptionProvider, boolean>>>({})
  const captionRef = useRef<HTMLTextAreaElement>(null)
  const dynamicCaptionRefs = useRef<Partial<Record<number, HTMLTextAreaElement | null>>>({})
  const { data: externalIntegrations } = useExternalIntegrations()
  const { data: fetchedTargets } = useScheduledPostTargets(scheduledPostId, { includeDiscarded: true })
  const { mutateAsync: createScheduledPostTarget } = useCreateScheduledPostTarget()
  const { mutateAsync: deleteScheduledPostTarget } = useDeleteScheduledPostTarget()
  const { connect } = useConnectAccountPopup({ showToast: true })
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()
  const { data: scheduledPost } = useScheduledPost({ id: scheduledPostId })
  const isCarousel = (scheduledPost?.media_objects?.length ?? 0) > 1
  const isVideo = scheduledPost?.media_objects?.some((m) => m.mime_type?.startsWith('video/')) ?? false

  const hasSyncedTargets = useRef(false)
  useEffect(() => {
    if (fetchedTargets && !hasSyncedTargets.current) {
      hasSyncedTargets.current = true
      setLocalTargets(fetchedTargets)
      setDynamicCaptionsByTargetId((prev) => {
        const next = { ...prev }
        fetchedTargets.forEach((target) => {
          if (!isDynamicCaptionProvider(target.provider)) return
          if (next[target.id] !== undefined) return
          next[target.id] = getCaptionForTarget(target, caption)
        })
        return next
      })
    }
  }, [fetchedTargets, caption])

  const hasXTarget = localTargets.some((target) => target.provider === ExternalIntegrationEnum.X)
  const xCharacterLimit = maxCharacters[ExternalIntegrationEnum.X]
  const weightedLength = twitter.parseTweet(localCaption).weightedLength
  const isOverXLimit = hasXTarget && weightedLength > xCharacterLimit

  const { mutateAsync: generateCaptions } = useGenerateCaption({ scheduledPostId })

  const connectedProviders = useMemo(() => {
    return new Set((externalIntegrations?.external_integrations ?? []).map((integration) => integration.provider))
  }, [externalIntegrations])

  const allowedProviders = useMemo(() => {
    const crossPostGroup = new Set(getCrossPostGroupForChannel(sourceChannel))
    return DYNAMIC_CAPTION_PROVIDERS.filter((provider) => crossPostGroup.has(provider))
  }, [sourceChannel])

  const dynamicCaptionRows = useMemo(() => {
    const availableIntegrations = externalIntegrations?.external_integrations ?? []

    return allowedProviders.map<DynamicCaptionRow>((provider) => {
      const allTargets = localTargets.filter((target) => target.provider === provider)
      const hasMultipleIntegrations = allTargets.some((t) => t.external_integration_id != null)
      const targets = hasMultipleIntegrations ? allTargets : allTargets.slice(0, 1)
      const providerIntegrations = availableIntegrations.filter((i) => i.provider === provider)

      const label = PROVIDER_LABELS[provider]
      const accountCaptionItems: DynamicCaptionAccountItem[] = (() => {
        if (targets.length > 0) {
          return targets.map((target, index) => {
            const integration = getIntegrationForTarget(target, availableIntegrations)
            const profileInfo = integration ? extractProfileInfo(integration) : null

            return {
              key: target.id,
              target,
              accountName: profileInfo?.name || `${label}${targets.length > 1 ? ` Account ${index + 1}` : ''}`,
              accountAvatar: profileInfo?.avatar || null,
              accountType: integration?.account_type,
              isIntegrationActive: integration?.is_active,
            }
          })
        }

        const firstIntegration = providerIntegrations[0]
        const profileInfo = firstIntegration ? extractProfileInfo(firstIntegration) : null
        return [
          {
            key: `${provider}-placeholder`,
            accountName: profileInfo?.name || label,
            accountAvatar: profileInfo?.avatar || null,
          },
        ]
      })()

      return {
        provider,
        label,
        targets,
        accountCaptionItems,
        initialCaption: targets.length > 0 ? getCaptionForTarget(targets[0], caption) : caption,
        isConnected: connectedProviders.has(provider),
        isEnabled: targets.some((t) => !t.discarded_at),
        hasAccountIcon: [
          ExternalIntegrationEnum.Facebook,
          ExternalIntegrationEnum.Instagram,
          ExternalIntegrationEnum.LinkedIn,
          ExternalIntegrationEnum.X,
          ExternalIntegrationEnum.YouTube,
          ExternalIntegrationEnum.TikTok,
        ].includes(provider),
      }
    })
  }, [allowedProviders, caption, connectedProviders, externalIntegrations, localTargets])

  const [dynamicCaptionsByTargetId, setDynamicCaptionsByTargetId] = useState<Partial<Record<number, string>>>(() => {
    return (scheduledPostTargets ?? []).reduce<Partial<Record<number, string>>>((captionsByTargetId, target) => {
      if (!isDynamicCaptionProvider(target.provider)) return captionsByTargetId

      captionsByTargetId[target.id] = getCaptionForTarget(target, caption)
      return captionsByTargetId
    }, {})
  })

  const [locationsByTargetId, setLocationsByTargetId] = useState<Partial<Record<number, PostLocation | null>>>(() => {
    return (scheduledPostTargets ?? []).reduce<Partial<Record<number, PostLocation | null>>>(
      (locationsByTarget, target) => {
        const loc = target.target_overrides?.location
        if (loc) locationsByTarget[target.id] = loc as PostLocation
        return locationsByTarget
      },
      {},
    )
  })

  const [tagsByTargetId, setTagsByTargetId] = useState<Partial<Record<number, PostTag[]>>>(() => {
    return (scheduledPostTargets ?? []).reduce<Partial<Record<number, PostTag[]>>>((tagsByTarget, target) => {
      const tags = target.target_overrides?.tags
      if (tags?.length) tagsByTarget[target.id] = tags as PostTag[]
      return tagsByTarget
    }, {})
  })

  const getDynamicCaptionValue = (target: ScheduledPostTarget) => {
    return dynamicCaptionsByTargetId[target.id] ?? getCaptionForTarget(target, caption)
  }

  const getProviderCaptionValue = (provider: DynamicCaptionProvider, targets: ScheduledPostTarget[]) => {
    const firstTarget = targets[0]
    if (!firstTarget) return caption

    return getDynamicCaptionValue(firstTarget)
  }

  // Fall back to the single caption editor unless some target would actually render an editable
  // row. A row is editable only when it has a target for a provider in this channel's group and
  // that target's integration is not inactive (mirrors `isCaptionEditable`); otherwise every row
  // is a read-only placeholder and the caption cannot be edited anywhere. The reported case was
  // a post whose only channel was never connected; a disconnected one behaves the same (BLZ-16550).
  const hasEditableDynamicTarget = localTargets.some((target) => {
    if (!isDynamicCaptionProvider(target.provider) || !allowedProviders.includes(target.provider)) return false

    const integration = getIntegrationForTarget(target, externalIntegrations?.external_integrations ?? [])

    return integration?.is_active !== false
  })
  const shouldShowDynamicCaptions = dynamicCaptionsEnabled && hasEditableDynamicTarget
  const isGeneratingAnyDynamicCaption =
    Object.values(generatingTargets).some(Boolean) || Object.values(updatingProviders).some(Boolean)

  const getOverLimitProviderNames = () => {
    if (shouldShowDynamicCaptions) {
      return dynamicCaptionRows
        .filter(
          (row) =>
            row.isEnabled &&
            row.targets.some((target) => getCharacterStats(row.provider, getDynamicCaptionValue(target)).isOverLimit),
        )
        .map((row) => PROVIDER_LABELS[row.provider])
    }
    return isOverXLimit ? ['X/Twitter'] : []
  }

  const performSave = async () => {
    setIsSaving(true)

    try {
      const payload: EditCaptionSavePayload = shouldShowDynamicCaptions
        ? {
            type: 'dynamic',
            captionsByProvider: dynamicCaptionRows.reduce<Partial<Record<ExternalIntegrationEnum, string>>>(
              (captionsByProvider, row) => {
                if (row.isEnabled) {
                  captionsByProvider[row.provider] = getProviderCaptionValue(row.provider, row.targets)
                }

                return captionsByProvider
              },
              {},
            ),
            captionsByTargetId: localTargets.reduce<Partial<Record<number, string>>>((captionsByTargetId, target) => {
              if (!isDynamicCaptionProvider(target.provider)) return captionsByTargetId

              captionsByTargetId[target.id] = getDynamicCaptionValue(target)
              return captionsByTargetId
            }, {}),
            locationsByTargetId,
            // If the post is a video and the target is Instagram, remove the x and y positions from the tags
            tagsByTargetId: Object.fromEntries(
              Object.entries(tagsByTargetId).map(([targetId, tags]) => {
                const target = localTargets.find((t) => t.id === Number(targetId))
                if (isVideo && target?.provider === ExternalIntegrationEnum.Instagram && tags) {
                  return [targetId, tags.map(({ x, y, ...rest }) => rest)]
                }
                return [targetId, tags]
              }),
            ),
            targets: localTargets.filter((target) => isDynamicCaptionProvider(target.provider)),
          }
        : localCaption

      await Promise.resolve(onSave(payload))
      closeModal()
    } catch (_error) {
      setIsSaving(false)
    }
  }

  const handleSave = () => {
    const overLimitNames = getOverLimitProviderNames()
    if (overLimitNames.length === 0) {
      performSave()
      return
    }

    const providerList = overLimitNames.join(', ')
    const confirmMessage = `${providerList} exceeded the character limit. Do you want to save anyway?`

    openModal(
      ConfirmModal,
      {
        title: 'Caption Over Limit',
        prompt: confirmMessage,
        cancelText: 'Cancel',
        confirmText: 'Save Anyway',
        onConfirm: performSave,
      },
      { newStack: true },
    )
  }

  const handleLegacyRegenerate = async () => {
    setLocalCaption('')
    setIsLegacyGenerating(true)

    try {
      const data = await generateCaptions({ userMessage: '' })
      setLocalCaption(data.caption)
    } finally {
      setIsLegacyGenerating(false)
      setTimeout(() => {
        captionRef.current?.focus()
      }, 0)
    }
  }

  const handleDynamicRegenerate = async (provider: DynamicCaptionProvider, targetId: number) => {
    const row = dynamicCaptionRows.find((captionRow) => captionRow.provider === provider)
    const target = row?.targets.find((captionTarget) => captionTarget.id === targetId)
    if (!row?.isEnabled || !target) return

    setGeneratingTargets((currentTargets) => ({ ...currentTargets, [targetId]: true }))

    try {
      const data = await generateCaptions({
        userMessage: '',
        provider,
      })

      setDynamicCaptionsByTargetId((currentCaptions) => ({
        ...currentCaptions,
        [target.id]: data.caption,
      }))
    } finally {
      setGeneratingTargets((currentTargets) => ({ ...currentTargets, [targetId]: false }))
      setTimeout(() => {
        dynamicCaptionRefs.current[target.id]?.focus()
      }, 0)
    }
  }

  const handleToggleProvider = async (provider: DynamicCaptionProvider, isChecked: boolean) => {
    const row = dynamicCaptionRows.find((captionRow) => captionRow.provider === provider)
    if (!row) return

    setUpdatingProviders((currentProviders) => ({ ...currentProviders, [provider]: true }))

    try {
      if (isChecked) {
        const providerCaption = getProviderCaptionValue(provider, row.targets)
        const createdTargets = await createScheduledPostTarget({
          scheduled_post_id: scheduledPostId,
          provider,
          target_overrides: {
            content: providerCaption,
          },
        })

        setLocalTargets((currentTargets) => [
          ...currentTargets.filter((target) => target.provider !== provider),
          ...createdTargets,
        ])
        setDynamicCaptionsByTargetId((currentCaptions) =>
          createdTargets.reduce<Partial<Record<number, string>>>(
            (nextCaptions, target) => {
              nextCaptions[target.id] = getCaptionForTarget(target, providerCaption)
              return nextCaptions
            },
            { ...currentCaptions },
          ),
        )
      } else {
        const providerTargets = localTargets.filter((target) => target.provider === provider)

        await Promise.all(
          providerTargets.map((target) =>
            deleteScheduledPostTarget({
              scheduled_post_id: scheduledPostId,
              target_id: target.id,
            }),
          ),
        )

        const discardedAt = new Date().toISOString()
        setLocalTargets((currentTargets) =>
          currentTargets.map((target) =>
            target.provider === provider ? { ...target, discarded_at: discardedAt } : target,
          ),
        )
      }
    } finally {
      setUpdatingProviders((currentProviders) => ({ ...currentProviders, [provider]: false }))
    }
  }

  return (
    <Modal.Root
      size={shouldShowDynamicCaptions ? 'lg' : 'sm'}
      onClose={closeModal}
      onPressOutside={closeModal}
      wrapperClassName={shouldShowDynamicCaptions ? Styles.modalWrapper : undefined}
      bodyClassName={
        shouldShowDynamicCaptions && allowedProviders.length >= 4 ? Styles.modalBody : Styles.modalBodyShrink
      }
    >
      <Modal.Header
        title={shouldShowDynamicCaptions ? 'Edit Captions' : 'Edit Caption'}
        actions={
          !shouldShowDynamicCaptions && (
            <Button
              variant="ghost-tertiary"
              size="lg"
              frontIcon={ArrowRefresh2}
              onPress={handleLegacyRegenerate}
              isDisabled={isLegacyGenerating || isSaving}
            >
              {isLegacyGenerating ? 'Generating...' : 'Regenerate'}
            </Button>
          )
        }
      />
      <Modal.Content className={shouldShowDynamicCaptions ? Styles.modalContent : undefined}>
        {shouldShowDynamicCaptions ? (
          <DynamicCaptionsModalContent
            rows={dynamicCaptionRows}
            generatingTargets={generatingTargets}
            updatingProviders={updatingProviders}
            isSaving={isSaving}
            isGeneratingAnyDynamicCaption={isGeneratingAnyDynamicCaption}
            onToggleProvider={handleToggleProvider}
            onRegenerate={handleDynamicRegenerate}
            onChangeCaption={(targetId, value) =>
              setDynamicCaptionsByTargetId((currentCaptions) => ({
                ...currentCaptions,
                [targetId]: value,
              }))
            }
            getDynamicCaptionValue={getDynamicCaptionValue}
            getCharacterStats={getCharacterStats}
            setDynamicCaptionRef={(targetId, element) => {
              dynamicCaptionRefs.current[targetId] = element
            }}
            connect={connect}
            locationsByTargetId={locationsByTargetId}
            onChangeLocation={(targetId, location) =>
              setLocationsByTargetId((current) => ({ ...current, [targetId]: location }))
            }
            tagsByTargetId={tagsByTargetId}
            onChangeTags={(targetId, tags) => setTagsByTargetId((current) => ({ ...current, [targetId]: tags }))}
            isCarousel={isCarousel}
            isVideo={isVideo}
          />
        ) : (
          <div className={Styles.content}>
            <div className={Styles.captionInput}>
              <div className={Styles.inputContainer}>
                <Input
                  name="caption"
                  value={localCaption}
                  isTextarea
                  autoFocus
                  isValid={!isOverXLimit}
                  rows={3}
                  maxHeight={400}
                  onChange={(e) => setLocalCaption(e.target.value)}
                  ref={captionRef}
                  className={cx({
                    [Styles.hiddenInput]: isLegacyGenerating,
                    [Styles.overLimitInput]: isOverXLimit,
                  })}
                />
                {isLegacyGenerating && (
                  <div className={Styles.skeletonOverlay}>
                    <LineSkeleton className={Styles.skeletonLine} width="85%" />
                    <LineSkeleton className={Styles.skeletonLine} width="75%" />
                    <LineSkeleton className={Styles.skeletonLine} width="90%" />
                  </div>
                )}
              </div>
              {hasXTarget && (
                <div className={cx(Styles.maxCharacters, { [Styles.overLimit]: isOverXLimit })}>
                  Character Count {weightedLength} / {xCharacterLimit}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={closeModal} isDisabled={isSaving}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          {shouldShowDynamicCaptions && (
            <Button
              variant="tertiary"
              size="lg"
              endIcon={LinkExternal}
              onPress={() => {
                if (!workspaceId) return
                closeAllModals()
                history.push({
                  pathname: generatePath(CONTENT_PREFERENCES_PATH, { workspaceId }),
                  hash: '#smart-captions',
                })
              }}
            >
              Learn more about smart caption
            </Button>
          )}
          <Modal.FooterButton
            variant="secondary"
            size="lg"
            onPress={handleSave}
            isDisabled={isSaving}
            aria-label={shouldShowDynamicCaptions ? 'Save Captions' : 'Save Caption'}
          >
            {shouldShowDynamicCaptions ? 'Save Captions' : 'Save Caption'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
